import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Star } from "lucide-react";

const testimonials = [
  {
    quote:
      "I had been carrying chronic stress for years before a colleague recommended Dr. Fiza. Her calm, structured approach helped me understand my anxiety triggers and regain control over my daily life.",
    name: "Priya S.",
    role: "Adult Client",
  },
  {
    quote:
      "After months of feeling disconnected from myself, sessions with Dr. Fiza Hasny gave me a safe place to process my emotions. I finally feel lighter, more hopeful, and capable of facing each day.",
    name: "Ankita R.",
    role: "Adult Client",
  },
  {
    quote:
      "My relationship with my partner was strained beyond repair—or so I thought. Working through communication patterns with Dr. Fiza helped us rebuild trust and emotional intimacy we thought we'd lost.",
    name: "Rohit A.",
    role: "Adult Client",
  },
  {
    quote:
      "I came to Dr. Fiza Hasny doubting every decision I made. Her guidance on self-confidence and personal growth has genuinely changed how I show up at work and in my relationships.",
    name: "Kavita M.",
    role: "Adult Client",
  },
  {
    quote:
      "The pressure of balancing a demanding career and personal life had left me exhausted. Dr. Fiza helped me set healthy boundaries and rediscover a sense of balance I didn't think was possible.",
    name: "Deepak V.",
    role: "Adult Client",
  },
  {
    quote:
      "Processing past trauma felt overwhelming until I found Dr. Fiza Hasny. Her trauma-focused approach was gentle yet effective, and I feel stronger and more grounded than I have in years.",
    name: "Neha S.",
    role: "Adult Client",
  },
  {
    quote:
      "During a difficult family transition, Dr. Fiza held space for each of us with equal empathy. Our sessions strengthened not just our communication, but our overall emotional wellness as a family.",
    name: "The Sharma Family",
    role: "Family",
  },
  {
    quote:
      "Dr. Fiza has a rare ability to make you feel truly heard. Through our work together on emotional healing, I've learned coping strategies that support me through life's hardest moments.",
    name: "Meena P.",
    role: "Adult Client",
  },
  {
    quote:
      "I wasn't sure therapy was for me until a friend insisted I speak with Dr. Fiza Hasny. She made mental wellness feel accessible, practical, and deeply personal—and I'm grateful I took that first step.",
    name: "Arjun K.",
    role: "Adult Client",
  },
];

const initials = (name) =>
  name
    .replace(/[^A-Za-z\s]/g, "")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

function Card({ t, i }) {
  return (
    <div
      className="rounded-2xl p-7 bg-white flex-shrink-0"
      style={{
        width: 320,
        boxShadow: "0 14px 36px -18px rgba(31,51,41,0.18)",
        border: "1px solid #e3efe9",
      }}
      data-testid={`testimonial-card-${i}`}
    >
      <div className="flex items-center gap-1 mb-4" style={{ color: "#e9b949" }}>
        {[...Array(5)].map((_, k) => (
          <Star key={k} size={16} fill="#e9b949" strokeWidth={0} />
        ))}
      </div>
      <p
        className="font-body italic text-[0.98rem] leading-relaxed mb-6 min-h-[112px]"
        style={{ color: "var(--color-text-dark)" }}
      >
        “{t.quote}”
      </p>
      <div className="flex items-center gap-3 pt-4 border-t border-[#e3efe9]">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center font-display font-bold text-white text-sm"
          style={{ background: "var(--color-accent)" }}
        >
          {initials(t.name)}
        </div>
        <div>
          <p
            className="font-display font-bold text-sm tracking-wide"
            style={{ color: "var(--color-accent)" }}
          >
            {t.name}
          </p>
          <p
            className="font-body text-xs"
            style={{ color: "var(--color-text-light)" }}
          >
            {t.role}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const doubled = [...testimonials, ...testimonials];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: "var(--color-secondary)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
        className="text-center mb-14 px-6"
      >
        <p
          className="font-body italic tracking-[0.3em] text-xs uppercase mb-4"
          style={{ color: "var(--color-accent)" }}
        >
          — Voices of Trust —
        </p>
        <h2
          className="font-display font-bold"
          style={{
            fontSize: "clamp(2rem, 5vw, 3.25rem)",
            color: "var(--color-text-dark)",
          }}
          data-testid="testimonials-title"
        >
          WHAT OUR CLIENTS SAY
        </h2>
        <div
          className="w-20 h-[3px] mx-auto mt-6 rounded-full"
          style={{ background: "var(--color-accent)" }}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="relative"
        data-testid="testimonials-marquee"
      >
        {/* Edge fade */}
        <div
          className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10"
          style={{
            background:
              "linear-gradient(to right, var(--color-secondary), rgba(212,232,224,0))",
          }}
        />
        <div
          className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10"
          style={{
            background:
              "linear-gradient(to left, var(--color-secondary), rgba(212,232,224,0))",
          }}
        />

        <div className="marquee-track gap-6 px-6 py-2">
          {doubled.map((t, i) => (
            <Card key={i} t={t} i={i} />
          ))}
        </div>
      </motion.div>
    </section>
  );
}
